import { AbsoluteFill, useCurrentScale, useVideoConfig } from "remotion";
import { TransitionSeries, linearTiming } from "@remotion/transitions";
import { I2VCompositionProps } from "@repo/api/editor";
import VoiceOver from "./components/VoiceOver.tsx";
import { getCompositionTimings } from "./utils/timing.ts";
import BackgroundMusic from "./components/BackgroundMusic.tsx";
import { useMemo } from "react";
import { getEndScene, getStartScene } from "@repo/api/utils/composition";
import MainScene from "./components/Scenes/MainScene.tsx";
import {
  getTransitionEasing,
  getTransitionPresentation,
} from "./utils/scene-transition.ts";
import StartScene from "./components/Scenes/StartScene.tsx";
import EndScene from "./components/Scenes/EndScene.tsx";
import { studioGlobals } from "./utils/studio-globals.ts";
import CompositionTransitionWrapper from "./components/CompositionTransitionBuilder/CompositionTransitionWrapper.tsx";
import { IconLoader2 } from "@tabler/icons-react";
import { FontContext } from "./utils/font.ts";

export const MyComposition = ({
  composition,
  sourceImages,
  animatedImages,
  voiceOvers,
  backgroundMusics,
  durationInFrames,
  isStudio,
  assetMedias,
  isPlaying,
  isFullscreen,
  fonts,
}: I2VCompositionProps) => {
  const { fps } = useVideoConfig();
  const scale = useCurrentScale() || 1;
  const borderRadius = Math.round(16 / scale);

  const timings = useMemo(
    () => getCompositionTimings(composition, animatedImages),
    [composition, animatedImages],
  );

  return (
    <FontContext.Provider value={fonts ?? {}}>
      <AbsoluteFill className="drag-none select-none">
        <LoadingIndicator isStudio={isStudio ?? false} scale={scale} />
        <AbsoluteFill
          style={{
            fontFamily: "Montserrat",
            wordWrap: "break-word",
            backgroundColor: "transparent",
            pointerEvents: "none",
            borderRadius: isStudio && !isFullscreen ? borderRadius : 0,
            outline:
              isStudio && !isFullscreen
                ? `${1 / scale}px solid var(--color-border)`
                : "none",
            overflow: "hidden",
          }}
        >
          <CompositionTransitionWrapper
            startTransition={composition.startTransition ?? null}
            endTransition={composition.endTransition ?? null}
            durationInFrames={durationInFrames}
          >
            <TransitionSeries from={0}>
              {timings.startScene && (
                <TransitionSeries.Sequence
                  key="startScene"
                  name="startScene"
                  durationInFrames={Math.max(
                    timings.startScene.durationInFrames,
                    timings.startScene.exitTransitionFrames + 1,
                  )}
                >
                  <StartScene
                    composition={composition}
                    timings={timings}
                    assetMedias={assetMedias}
                    isStudio={isStudio ?? false}
                    isPlaying={isPlaying ?? false}
                  />
                </TransitionSeries.Sequence>
              )}

              {(timings.startScene?.exitTransitionFrames ?? 0) > 0 && (
                <TransitionSeries.Transition
                  key={"startToMainSceneTransition"}
                  presentation={getTransitionPresentation(
                    getStartScene(composition)!.exitTransition!,
                    "startScene",
                  )}
                  timing={linearTiming({
                    durationInFrames: timings.startScene!.exitTransitionFrames,
                    easing: getTransitionEasing(
                      getStartScene(composition)!.exitTransition!,
                    ),
                  })}
                />
              )}

              <TransitionSeries.Sequence
                key="mainScene"
                name="mainScene"
                durationInFrames={Math.max(
                  timings.mainScene.durationInFrames,
                  timings.mainScene.entryTransitionFrames +
                    timings.mainScene.exitTransitionFrames +
                    1,
                )}
              >
                <MainScene
                  composition={composition}
                  sourceImages={sourceImages}
                  animatedImages={animatedImages}
                  timings={timings}
                  assetMedias={assetMedias}
                  voiceOvers={voiceOvers}
                  fps={fps}
                  isPlaying={isPlaying ?? false}
                />
              </TransitionSeries.Sequence>

              {(timings.endScene?.entryTransitionFrames ?? 0) > 0 && (
                <TransitionSeries.Transition
                  key={"mainSceneToEndSceneTransition"}
                  presentation={getTransitionPresentation(
                    getEndScene(composition)!.entryTransition!,
                    "endScene",
                  )}
                  timing={linearTiming({
                    durationInFrames: timings.endScene!.entryTransitionFrames,
                    easing: getTransitionEasing(
                      getEndScene(composition)!.entryTransition!,
                    ),
                  })}
                />
              )}

              {timings.endScene && (
                <TransitionSeries.Sequence
                  key="endScene"
                  name="endScene"
                  durationInFrames={Math.max(
                    timings.endScene.durationInFrames,
                    timings.endScene.entryTransitionFrames + 1,
                  )}
                >
                  <EndScene
                    composition={composition}
                    timings={timings}
                    assetMedias={assetMedias}
                    isStudio={isStudio ?? false}
                    isPlaying={isPlaying ?? false}
                  />
                </TransitionSeries.Sequence>
              )}
            </TransitionSeries>

            <VoiceOver
              timings={timings}
              compositionVoiceOver={composition.voiceOver}
              voiceOvers={voiceOvers}
              compositionCaptions={composition.captions}
              aspectRatio={composition.aspectRatio}
            />

            <BackgroundMusic
              composition={composition}
              backgroundMusics={backgroundMusics}
              durationInFrames={durationInFrames}
              voiceOvers={voiceOvers}
              timings={timings}
            />
          </CompositionTransitionWrapper>
        </AbsoluteFill>

        {studioGlobals.ElementControlsLayer && !isPlaying && !isFullscreen && (
          <studioGlobals.ElementControlsLayer composition={composition} />
        )}
      </AbsoluteFill>
    </FontContext.Provider>
  );
};

function LoadingIndicator({
  isStudio,
  scale,
}: {
  isStudio: boolean;
  scale: number;
}) {
  const iconSize = Math.round(20 / scale);
  const fontSizeHeader = Math.round(14 / scale);
  const fontSizeSubheader = Math.round(14 / scale);
  const marginTop = Math.round(6 / scale);
  const paddingX = Math.round(24 / scale);

  if (!isStudio) {
    return null;
  }

  return (
    <div
      className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center"
      style={{
        paddingLeft: paddingX,
        paddingRight: paddingX,
      }}
    >
      <IconLoader2
        size={iconSize}
        className="animate-spin"
        color="url(#icon-gradient)"
      />
      <p
        className="text-text-primary text-sm font-semibold text-center"
        style={{ fontSize: fontSizeHeader, marginTop }}
      >
        Loading video…
      </p>
      <p
        className="text-text-secondary text-sm text-center"
        style={{ fontSize: fontSizeSubheader }}
      >
        This may take longer on slower connections
      </p>
    </div>
  );
}
